import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
const PRIMARY = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249',
};
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[600_8],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: '#fff',
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  header: {
    bgColor: '#5A1818',
    menuBg: '#01a101',
    menu: '#C13333',
    deposit_btn: {
      bg: '#AC0000',
      color: '#fff',
    },
    login_btn: {
      border: '#FF1500',
      bg: 'linear-gradient(0deg, rgb(170, 37, 27), rgb(235, 68, 58))',
      color: '#fff',
    },
    register_btn: {
      border: '#FF1500',
      bg: '#711E1E',
      color: '#fff',
    },
    balance_button: {
      bg: '#159e1a',
      color: '#fff',
    },
  },

  navBarVertical: {
    bg: 'linear-gradient(0deg, #00e495, #b2ff01)',
    menu: {
      bgColor: '#01a101',
      color: '#fff',
    },
  },

  bottomMenu: {
    bgColor: '#830000',
    color: `#6E2100`,
    depositBg: `linear-gradient(to bottom, #CDCDCD 0%, #FFFFFF 100%)`,
    depositBorder: '#f2c556',
  },

  home: {
    announcement: {
      bg: '#5A1818',
      color: '#d4af37',
    },
    tab: {
      borderBottom: '#9B7100',
      bgActive: '#ff0000',
      bgInActive: 'transparent',
      textActive: 'yellow',
      textInactive: '#000',
    },
    sort: {
      labelColor: '#c7fe27',
    },
  },

  footer: {
    bgColor: '#711E1E',
  },

  visitor: {
    dialog: {
      title: '#000',
      loginDirectly: '#000',
      footerText: 'grey',
      hr: `#000`,
      otherText: '#000',
    },
    icon: '#000',
    input: {
      border: 'red',
      color: '#000',
      placeholder: '#5f5f5f',
    },
    link: '#edb001',
    errorText: '#bd0000',
    login: {
      buttonBg: 'linear-gradient(90deg, #F00101, #9F0101)',
      color: '#fff',
      border: '#fce800',
    },
    register: {
      buttonBg: 'linear-gradient(90deg, #F00101, #9F0101)',
      color: '#fff',
      border: '#fce800',
    },
  },

  transactionDialog: {
    bg: '#0d1a8c',
    border: '#c7fe27',
    tab: {
      bg: '#EEEEEE',
      active: WARNING.main,
      inactive: '#EEEEEE',
      activeTxt: '#000',
      inactiveTxt: '#858585',
    },
    deposit: {
      copy: '#000',
      inputLabel: '#000',
      smallText: '#000',
      largeText: '#000',
      suggestedTopupBg: '#FFD700',
      suggestedTopupTxt: '#000',
      smallPromoBox: {
        bg: '#E63946',
        color: '#fff',
      },
      bonusBox: {
        bg: '#FFD700',
        color: '#000',
        border: 'transparent',
      },
      select: {
        bg: '#fff',
        color: '#000',
        border: 'rgb(253, 169, 45)',
      },
      selectedBankBox: {
        bg: alpha('#FEF4D4', 0.9),
        border: 'rgb(253, 169, 45)',
      },
      depositInputBox: {
        bg: '#fff',
        border: 'rgb(253, 169, 45)',
      },
      depositInput: {
        color: '#000',
      },
      bankRefNumber: {
        color: '#000',
        border: 'rgb(253, 169, 45)',
        outlineBg: '#000',
        placeholder: 'rgba(0, 0, 0, .2)',
      },
      confirmBtn: 'linear-gradient(90deg, #F00101 0%, #9F0101 100%)',
      bepositBtnText: '#fff',
      depositAmt: '#fff',
    },
    withdraw: {
      text: '#000',
      suggestedWthdrwBg: '#FFD700',
      availableBalanceTxt: 'rgba(0, 0, 0, 0.85)',
      withdrawInputBox: {
        bg: '#fff',
        border: 'rgb(253, 169, 45)',
        shadow: 'rgba(23,125,220,.2)',
      },
      noBankAccount: '#000',
      withdrawText: '#fff',
      confirmBtnBg: 'rgb(237, 29, 73)',
    },
  },

  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;
